import { fragmentsPermissions } from '@app/modules/fragments/fragments-permissions';
import { useFragmentsDeliveryGetDeliveryStatus } from '@mi-front/api-v2';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DeliveryStart } from './DeliveryStart';

import { IfPermission } from '@app/core/auth/authorization/components/IfPermission';
import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { MutedAlert } from '../../../../../../../components/MutedAlert';
import { useFragment } from '../../../hooks/useFragment';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

/**
 * Shows whether a delivery has been started for the fragment and its asset statuses.
 * `deliveryStatus.enabled` means a delivery exists (not the station feature flag).
 */
export function DeliveryStatusInfo() {
  const { fragment } = useFragment();
  const finder = useStationsFinder();

  const { data: deliveryStatus, refetch } = useFragmentsDeliveryGetDeliveryStatus(fragment.id, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;

        // If no assets or any asset is in progress, refetch every 5 seconds
        if (
          data?.enabled &&
          (!data?.assets?.length || data.assets.some((asset) => asset.status === 'IN_PROGRESS'))
        ) {
          return 5000;
        }

        return false;
      },
    },
  });

  const hasDelivery = !!deliveryStatus?.enabled;

  if (!hasDelivery) {
    return (
      <MutedAlert
        sx={{
          '.MuiAlert-message': {
            overflow: 'visible',
          },
        }}
      >
        <AlertTitle>
          <FormattedMessage
            id="fragments.deliveryStatus.none"
            defaultMessage="Fragmento no exportado"
          />
        </AlertTitle>

        {fragment.mediaCompletedAt && (
          <IfPermission permission={fragmentsPermissions.Fragments_Delivery}>
            <Box mt={2}>
              <DeliveryStart
                fragment={fragment}
                onStarted={() => {
                  refetch();
                }}
              />
            </Box>
          </IfPermission>
        )}
      </MutedAlert>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS':
        return <HourglassEmptyIcon />;
      case 'COMPLETED':
        return <DoneIcon />;
      case 'FAILED':
        return <ErrorIcon />;
      default:
        return <HourglassEmptyIcon />;
    }
  };

  return (
    <Alert severity="success">
      <AlertTitle>
        <FormattedMessage id="fragments.exportStatus.ready" defaultMessage="Listo" />
      </AlertTitle>
      <div>
        {deliveryStatus.options?.targetStationId && (
          <Box mt={1} mb={2}>
            <FormattedMessage
              id="fragments.deliveryStatus.destinationStationLabel"
              defaultMessage="Destination station"
            />
            : <strong>{finder(deliveryStatus.options.targetStationId)?.name}</strong>
          </Box>
        )}

        <div>
          {deliveryStatus.assets
            ?.sort((a, b) => a.type.localeCompare(b.type))
            .map((asset) => (
              <CustomWidthTooltip
                title={
                  asset.error ? (
                    ''
                  ) : (
                    <FormattedMessage
                      id="fragments.delivery.savedAt"
                      defaultMessage="Guardado en {uri}"
                      values={{ uri: asset.targetUri }}
                    />
                  )
                }
                key={asset.id}
              >
                <Chip
                  label={asset.type.toUpperCase()}
                  color={getStatusColor(asset.status)}
                  icon={getStatusIcon(asset.status)}
                  size="small"
                  sx={{
                    mr: 1,
                  }}
                />
              </CustomWidthTooltip>
            ))}
        </div>
      </div>
    </Alert>
  );
}
